/*
 * Resolves only approved tariff coordinates. Customer input is normalized to exact millimetres;
 * checkout remains closed whenever Shopify cannot collect the full configured amount.
 */
(function () {
  'use strict';

  var OUTCOMES = {
    ready: 'ready',
    incomplete: 'incomplete',
    underMinimum: 'under-minimum',
    overMaximum: 'over-maximum',
    customColour: 'custom-colour',
    uncaptured: 'uncaptured',
    forceQuote: 'force-quote'
  };

  var SURCHARGES = Object.freeze({
    magneticClosure: Object.freeze({ label: 'Magnetische sluiting', cents: 2500 }),
    pollenMesh: Object.freeze({ label: 'Pollenwerend gaas', cents: 3500 }),
    customRal: Object.freeze({ label: 'Andere RAL', cents: 14900 })
  });

  var POLLEN_ADD_ON_SOURCE_ID = 'hvo-pollen-mesh';
  var POLLEN_COMPATIBLE_MAIN_SOURCE_IDS = Object.freeze(['232', '233', '8', '9', '709', '751']);
  var POLLEN_CONFIGURATION_PROPERTY = 'Pollenwerend gaas';
  var POLLEN_SELECTED_VALUE = 'Ja, + €35';

  function validPollenAddonContract(addon, mainSourceId) {
    if (!addon || addon.sourceId !== POLLEN_ADD_ON_SOURCE_ID) return false;
    if (!addon.available || Number(addon.priceCents) !== SURCHARGES.pollenMesh.cents) return false;
    if (!/^[1-9]\d*$/.test(String(addon.variantId || ''))) return false;
    var policy = addon.policy;
    if (!policy || policy.sourceId !== POLLEN_ADD_ON_SOURCE_ID) return false;
    if (
      policy.configurationProperty !== POLLEN_CONFIGURATION_PROPERTY
      || policy.selectedValue !== POLLEN_SELECTED_VALUE
      || !policy.quantityRule
      || policy.quantityRule.mode !== 'same-as-main-quantity'
    ) return false;
    var compatible = policy.compatibleMainSourceIds;
    return Array.isArray(compatible)
      && compatible.length === POLLEN_COMPATIBLE_MAIN_SOURCE_IDS.length
      && compatible.every(function (sourceId, index) {
        return String(sourceId) === POLLEN_COMPATIBLE_MAIN_SOURCE_IDS[index];
      })
      && compatible.indexOf(String(mainSourceId || '')) >= 0;
  }

  /*
   * HVO retail policy: every listed standard RAL uses the same approved customer tariff.
   * RAL 9016 is the retail reference only; the selected RAL stays untouched in the order property.
   * The legacy 1015 fallback preserves an existing retail lookup and never rewrites supplier data.
   */
  var STANDARD_RETAIL_REFERENCE_RAL = '9016';
  var STANDARD_RETAIL_COLOURS = Object.freeze({
    '9016': true,
    '9010': true,
    '9001': true,
    '7016': true,
    '9005': true
  });

  function readJson(selector, root) {
    var node = (root || document).querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function roundUpToStep(value, step) {
    if (!step || step <= 0) return value;
    return Math.ceil((value - Number.EPSILON) / step) * step;
  }

  function parseCentimetresToMillimetres(value) {
    var normalized = String(value || '').trim().replace(',', '.');
    if (!/^\d+(?:\.\d)?$/.test(normalized)) return null;
    var millimetres = Math.round(Number(normalized) * 10);
    return Number.isSafeInteger(millimetres) ? millimetres : null;
  }

  function centimetresToMillimetres(value) {
    var centimetres = Number(value);
    if (!Number.isFinite(centimetres)) return null;
    var millimetres = Math.round(centimetres * 10);
    return Number.isSafeInteger(millimetres) ? millimetres : null;
  }

  function normalizeRalCode(value) {
    return String(value || '').toUpperCase().replace(/^RAL\s*/, '').replace(/\s+/g, '');
  }

  function findProfileIndex(profiles, ralCode) {
    return profiles.findIndex(function (profile) {
      return normalizeRalCode(profile) === ralCode;
    });
  }

  function resolveRetailReferenceIndex(profiles) {
    var referenceIndex = findProfileIndex(profiles, STANDARD_RETAIL_REFERENCE_RAL);
    if (referenceIndex >= 0) return referenceIndex;
    if (normalizeRalCode(profiles[0]) === '1015') return 0;
    var fallbackCodes = ['7016', '9005', '9010', '9001'];
    for (var index = 0; index < fallbackCodes.length; index += 1) {
      var fallbackIndex = findProfileIndex(profiles, fallbackCodes[index]);
      if (fallbackIndex >= 0) return fallbackIndex;
    }
    return -1;
  }

  function resolveProfileIndex(profiles, selectedProfile) {
    var selectedCode = normalizeRalCode(selectedProfile);
    if (STANDARD_RETAIL_COLOURS[selectedCode]) return resolveRetailReferenceIndex(profiles);
    return findProfileIndex(profiles, selectedCode);
  }

  function isNonBlankString(value) {
    return typeof value === 'string' && value.trim() !== '';
  }

  function hasPricingContractMetadata(tiers) {
    return Object.keys(tiers || {}).some(function (tierLabel) {
      var variant = tiers[tierLabel];
      if (!variant) return false;
      var hasPricingKeys = Object.prototype.hasOwnProperty.call(variant, 'pricingKeys')
        && variant.pricingKeys !== null
        && typeof variant.pricingKeys !== 'undefined';
      var hasEvidenceVersion = Object.prototype.hasOwnProperty.call(variant, 'pricingEvidenceVersion')
        && variant.pricingEvidenceVersion !== null
        && typeof variant.pricingEvidenceVersion !== 'undefined';
      return hasPricingKeys || hasEvidenceVersion;
    });
  }

  function hasAuthoritativeLookupContract(lookup) {
    return Boolean(lookup && lookup.schemaVersion === 2);
  }

  function configurationKeyForCoordinate(lookup, coordinate) {
    if (!lookup || !Array.isArray(coordinate) || coordinate.length < 5) return null;
    if (!Number.isFinite(Number(coordinate[0])) || !Number.isFinite(Number(coordinate[1]))) return null;
    var profiles = lookup.profiles || [];
    var selections = lookup.selections || [];
    if (
      !Object.prototype.hasOwnProperty.call(profiles, coordinate[2])
      || !Object.prototype.hasOwnProperty.call(selections, coordinate[3])
    ) return null;
    var profile = profiles[coordinate[2]];
    var selection = selections[coordinate[3]];
    if (!isNonBlankString(profile) || typeof selection !== 'string') return null;
    return [coordinate[0], coordinate[1], profile, selection].join('|');
  }

  function validatePricingContract(tiers, lookup, variant, coordinate, configurationKey) {
    if (!hasPricingContractMetadata(tiers)) {
      if (!hasAuthoritativeLookupContract(lookup)) {
        return { valid: true, configurationKey: '', evidenceVersion: '' };
      }

      var authoritativeEvidenceVersion = lookup.evidenceVersion;
      var authoritativeTiers = lookup.tiers;
      var authoritativeTierIndex = Array.isArray(coordinate) ? coordinate[4] : null;
      if (
        !configurationKey
        || !isNonBlankString(authoritativeEvidenceVersion)
        || !Array.isArray(coordinate)
        || coordinate.length !== 5
        || !Array.isArray(authoritativeTiers)
        || !Number.isInteger(authoritativeTierIndex)
        || !Object.prototype.hasOwnProperty.call(authoritativeTiers, authoritativeTierIndex)
        || !isNonBlankString(authoritativeTiers[authoritativeTierIndex])
        || !tiers
        || tiers[authoritativeTiers[authoritativeTierIndex]] !== variant
      ) {
        return { valid: false, configurationKey: '', evidenceVersion: '' };
      }
      return {
        valid: true,
        configurationKey: configurationKey,
        evidenceVersion: authoritativeEvidenceVersion
      };
    }

    var lookupEvidenceVersion = lookup && lookup.evidenceVersion;
    var tierLabels = Array.from(new Set(lookup && Array.isArray(lookup.tiers) ? lookup.tiers : []));
    if (!configurationKey || !isNonBlankString(lookupEvidenceVersion) || !tierLabels.length) {
      return { valid: false, configurationKey: '', evidenceVersion: '' };
    }

    var owners = [];
    var metadataValid = tierLabels.every(function (tierLabel) {
      var tierVariant = tiers[tierLabel];
      if (
        !tierVariant
        || !Array.isArray(tierVariant.pricingKeys)
        || !tierVariant.pricingKeys.length
        || !isNonBlankString(tierVariant.pricingEvidenceVersion)
        || tierVariant.pricingEvidenceVersion !== lookupEvidenceVersion
      ) {
        return false;
      }

      var seen = Object.create(null);
      var keysValid = tierVariant.pricingKeys.every(function (pricingKey) {
        if (!isNonBlankString(pricingKey) || seen[pricingKey]) return false;
        seen[pricingKey] = true;
        return true;
      });
      if (!keysValid) return false;
      if (seen[configurationKey]) owners.push(tierVariant);
      return true;
    });

    if (!metadataValid || owners.length !== 1 || owners[0] !== variant) {
      return { valid: false, configurationKey: '', evidenceVersion: '' };
    }
    return {
      valid: true,
      configurationKey: configurationKey,
      evidenceVersion: lookupEvidenceVersion
    };
  }
  function hasSurcharge(answers, key) {
    return (answers.surcharges || []).some(function (surcharge) {
      return surcharge.key === key;
    });
  }

  function validateNativeMagneticContract(tiers, lookup, answers, variant, coordinate, pricingContract) {
    var marker = lookup && lookup.nativeMagneticContract;
    var magneticRequested = hasSurcharge(answers, 'magneticClosure')
      || Boolean(marker && answers.selection === marker.magneticSelectionSourceId)
      || answers.selection === '3970';
    if (!magneticRequested) {
      return { required: false, valid: true, surchargeCents: 0, standardPriceCents: null };
    }
    if (
      !marker
      || marker.schemaVersion !== 1
      || marker.policyId !== 'source-232-native-magnetic-standard-plus-2500'
      || marker.sourceId !== '232'
      || marker.standardSelection !== 'urunalanlar=3969'
      || marker.standardSelectionSourceId !== '3969'
      || marker.magneticSelection !== 'urunalanlar=3970'
      || marker.magneticSelectionSourceId !== '3970'
      || marker.surchargeCents !== 2500
      || !isNonBlankString(marker.supplierEvidenceVersion)
      || !isNonBlankString(marker.retailEvidenceVersion)
      || marker.retailEvidenceVersion !== lookup.evidenceVersion
      || answers.selection !== marker.magneticSelectionSourceId
      || !pricingContract.valid
      || !Array.isArray(coordinate)
      || coordinate.length !== 5
    ) {
      return { required: true, valid: false, surchargeCents: 2500, standardPriceCents: null };
    }
    var standardSelectionIndex = (lookup.selections || []).indexOf(marker.standardSelection);
    var magneticSelectionIndex = (lookup.selections || []).indexOf(marker.magneticSelection);
    if (standardSelectionIndex < 0 || coordinate[3] !== magneticSelectionIndex) {
      return { required: true, valid: false, surchargeCents: 2500, standardPriceCents: null };
    }
    var standardCoordinate = (lookup.coordinates || []).find(function (candidate) {
      return candidate[0] === coordinate[0]
        && candidate[1] === coordinate[1]
        && candidate[2] === coordinate[2]
        && candidate[3] === standardSelectionIndex;
    });
    if (!standardCoordinate) {
      return { required: true, valid: false, surchargeCents: 2500, standardPriceCents: null };
    }
    var standardTier = (lookup.tiers || [])[standardCoordinate[4]];
    var standardVariant = tiers[standardTier];
    var standardKey = configurationKeyForCoordinate(lookup, standardCoordinate);
    var standardContract = validatePricingContract(
      tiers,
      lookup,
      standardVariant,
      standardCoordinate,
      standardKey
    );
    var standardPriceCents = standardVariant && Number(standardVariant.price);
    var magneticPriceCents = Number(variant && variant.price);
    var valid = Boolean(
      standardVariant
      && standardVariant.available
      && standardVariant.id
      && Number.isFinite(standardPriceCents)
      && standardPriceCents > 0
      && Number.isFinite(magneticPriceCents)
      && magneticPriceCents === standardPriceCents + marker.surchargeCents
      && standardContract.valid
      && pricingContract.configurationKey.slice(-marker.magneticSelection.length - 1)
        === '|' + marker.magneticSelection
    );
    return {
      required: true,
      valid: valid,
      surchargeCents: marker.surchargeCents,
      standardPriceCents: valid ? standardPriceCents : null
    };
  }


  function formatCentimetres(millimetres) {
    return (millimetres / 10).toFixed(millimetres % 10 === 0 ? 0 : 1);
  }

  function syncSelectedColourLabel(form) {
    var label = form.querySelector('[data-colour-selection]');
    if (!label) return;
    var selected = form.querySelector('[data-colour-standard]:checked');
    label.textContent = selected ? selected.value : 'Kies een profielkleur';
  }

  function hasValidPayloadNumber(input, integer) {
    if (!input) return false;
    var text = String(input.value || '').trim();
    if (integer) return /^[1-9]\d*$/.test(text);
    return /^\d+(?:\.\d)?$/.test(text) && Number(text) > 0;
  }

  function sanitizeShopifyDescription(value) {
    if (typeof value !== 'string') return null;
    var description = value.trim();
    if (
      !description
      || description.length > 160
      || /[\r\n\t]/.test(description)
      || /(?:https?:\/\/|www\.)/i.test(description)
      || /\b[A-Za-z0-9]{40,}\b/.test(description)
      || !/\s/.test(description)
      || /\b(?:token|secret|password|bearer|session|api[ -]?key)\b/i.test(description)
      || !/^[A-Za-z0-9À-ÖØ-öø-ÿ€.,:;!?'"()&%+\-/ ]+$/.test(description)
    ) return null;
    return description;
  }

  function cartDiagnostic(stage, status, description, flags) {
    var diagnostic = {
      stage: stage,
      status: Number.isInteger(status) ? status : null
    };
    if (flags) {
      diagnostic.configurationKeySubmitted = Boolean(flags.configurationKeySubmitted);
      diagnostic.pricingEvidenceVersionSubmitted = Boolean(flags.pricingEvidenceVersionSubmitted);
      diagnostic.variantPricingMetadataAvailable = Boolean(flags.variantPricingMetadataAvailable);
      diagnostic.lookupPricingMetadataAvailable = Boolean(flags.lookupPricingMetadataAvailable);
      if (flags.debug) diagnostic.debug = flags.debug;
    }
    if (description) diagnostic.description = description;
    return diagnostic;
  }

  function readFormDataField(formData, name) {
    var count = 0;
    var onlyValue = null;
    var hasNonBlankValue = false;
    if (!formData || typeof formData.forEach !== 'function') {
      return { count: count, value: onlyValue, hasNonBlankValue: hasNonBlankValue };
    }
    formData.forEach(function (value, entryName) {
      if (entryName !== name) return;
      count += 1;
      if (isNonBlankString(value)) hasNonBlankValue = true;
      onlyValue = count === 1 && typeof value === 'string' ? value : null;
    });
    if (count !== 1) onlyValue = null;
    return { count: count, value: onlyValue, hasNonBlankValue: hasNonBlankValue };
  }

  function formDataHasNonBlankValue(formData, name) {
    return readFormDataField(formData, name).hasNonBlankValue;
  }

  function cartContractDebugEnabled() {
    try {
      var url = new URL(window.location.href);
      var previewThemeId = url.searchParams.get('preview_theme_id') || '';
      return url.searchParams.get('hvo_cart_debug') === '1'
        && /^[1-9]\d{0,19}$/.test(previewThemeId);
    } catch (error) {
      return false;
    }
  }

  function safePublicVariantId(value) {
    return typeof value === 'string' && /^[1-9]\d{0,19}$/.test(value) ? value : null;
  }

  function safeGeneratedConfigurationKey(value) {
    if (typeof value !== 'string' || value.length > 160) return null;
    return /^[1-9]\d{0,3}(?:\.\d{1,3})?\|[1-9]\d{0,3}(?:\.\d{1,3})?\|[A-Za-z0-9-]{1,20}\|(?:[A-Za-z0-9_-]+=[A-Za-z0-9+_.-]+)?$/.test(value)
      ? value
      : null;
  }

  function safePricingEvidenceVersion(value) {
    if (typeof value !== 'string' || value.length > 96) return null;
    return /^source-[1-9]\d{0,9}-(?:complete|canary)-[a-f0-9]{12}$/.test(value)
      ? value
      : null;
  }

  function createLinkedLineIdentity() {
    if (
      !window.crypto
      || typeof window.crypto.randomUUID !== 'function'
    ) return null;
    return window.crypto.randomUUID();
  }

  function buildPollenCartPayload(formData, pollenAddon) {
    var variantField = readFormDataField(formData, 'id');
    var quantityField = readFormDataField(formData, 'quantity');
    var variantId = safePublicVariantId(variantField.value);
    var quantity = Number(quantityField.value);
    if (
      !variantId
      || quantityField.count !== 1
      || !Number.isSafeInteger(quantity)
      || quantity < 1
    ) return null;

    var properties = {};
    var invalidProperty = false;
    formData.forEach(function (value, name) {
      var property = name.match(/^properties\[(.+)\]$/);
      if (!property || typeof value !== 'string' || value === '') return;
      if (Object.prototype.hasOwnProperty.call(properties, property[1])) {
        invalidProperty = true;
        return;
      }
      properties[property[1]] = value;
    });
    if (
      invalidProperty
      || properties[POLLEN_CONFIGURATION_PROPERTY] !== POLLEN_SELECTED_VALUE
    ) return null;

    var identity = createLinkedLineIdentity();
    if (!identity) return null;
    properties._hvo_line_identity = identity;
    return {
      items: [
        {
          id: Number(variantId),
          quantity: quantity,
          properties: properties
        },
        {
          id: Number(pollenAddon.variantId),
          quantity: quantity,
          properties: {
            _hvo_parent_identity: identity
          }
        }
      ]
    };
  }

  function cartContractDebugDetails(formData, lookup, tiers, currentContract) {
    var variantField = readFormDataField(formData, 'id');
    var keyField = readFormDataField(formData, 'properties[_hvo_configuration_key]');
    var evidenceField = readFormDataField(formData, 'properties[_hvo_pricing_evidence_version]');
    var variantId = safePublicVariantId(variantField.value);
    var configurationKey = safeGeneratedConfigurationKey(keyField.value);
    var evidenceVersion = safePricingEvidenceVersion(evidenceField.value);
    var selectedVariant = currentContract && currentContract.variant;
    var ownerCount = configurationKey ? Object.keys(tiers || {}).filter(function (tierLabel) {
      var tierVariant = tiers[tierLabel];
      return tierVariant
        && Array.isArray(tierVariant.pricingKeys)
        && tierVariant.pricingKeys.indexOf(configurationKey) >= 0;
    }).length : 0;
    var details = {
      variantIdFieldCount: variantField.count,
      configurationKeyFieldCount: keyField.count,
      pricingEvidenceVersionFieldCount: evidenceField.count,
      variantIdMatchesGenerated: Boolean(
        variantId
        && currentContract
        && variantId === currentContract.variantId
      ),
      configurationKeyMatchesGenerated: Boolean(
        configurationKey
        && currentContract
        && configurationKey === currentContract.configurationKey
      ),
      pricingEvidenceVersionMatchesGenerated: Boolean(
        evidenceVersion
        && currentContract
        && evidenceVersion === currentContract.evidenceVersion
      ),
      selectedVariantOwnsConfigurationKey: Boolean(
        configurationKey
        && selectedVariant
        && Array.isArray(selectedVariant.pricingKeys)
        && selectedVariant.pricingKeys.indexOf(configurationKey) >= 0
      ),
      configurationKeyOwnerCount: ownerCount,
      submittedEvidenceMatchesSelectedVariantMetadata: Boolean(
        evidenceVersion
        && selectedVariant
        && selectedVariant.pricingEvidenceVersion === evidenceVersion
      ),
      submittedEvidenceMatchesLookupMetadata: Boolean(
        evidenceVersion
        && lookup
        && lookup.evidenceVersion === evidenceVersion
      )
    };
    if (variantId) details.variantId = variantId;
    if (configurationKey) details.configurationKey = configurationKey;
    if (evidenceVersion) details.pricingEvidenceVersion = evidenceVersion;
    return details;
  }

  function cartContractDiagnosticFlags(formData, lookup, tiers, currentContract) {
    var flags = {
      configurationKeySubmitted: formDataHasNonBlankValue(
        formData,
        'properties[_hvo_configuration_key]'
      ),
      pricingEvidenceVersionSubmitted: formDataHasNonBlankValue(
        formData,
        'properties[_hvo_pricing_evidence_version]'
      ),
      variantPricingMetadataAvailable: hasPricingContractMetadata(tiers),
      lookupPricingMetadataAvailable: hasAuthoritativeLookupContract(lookup)
        && isNonBlankString(lookup.evidenceVersion)
    };
    if (cartContractDebugEnabled()) {
      flags.debug = cartContractDebugDetails(formData, lookup, tiers, currentContract);
    }
    return flags;
  }

  function reportCartDiagnostic(diagnostic) {
    if (typeof console === 'undefined' || !console || typeof console.warn !== 'function') return;
    console.warn('HVO cart request failed ' + JSON.stringify(diagnostic));
  }

  function readAddFailureDiagnostic(response, flags) {
    var fallback = cartDiagnostic('add', response.status, null, flags);
    return Promise.resolve()
      .then(function () { return response.json(); })
      .then(function (payload) {
        var description = sanitizeShopifyDescription(payload && payload.description);
        return description ? cartDiagnostic('add', response.status, description, flags) : fallback;
      }, function () {
        return fallback;
      });
  }

  function formatEuros(cents) {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(cents / 100);
  }

  function Configurator(form) {
    this.form = form;
    this.root = form.closest && form.closest('[id^="shopify-section-"]') || document;
    this.lookup = readJson('[data-pricing-lookup]', this.root);
    this.tiers = readJson('[data-price-tier-variants]', this.root) || {};
    this.bounds = readJson('[data-product-bounds]', this.root) || {};
    this.strings = readJson('[data-product-translations]', this.root) || {};
    this.checkoutGate = Boolean(this.bounds.checkoutGate || form.hasAttribute('data-checkout-gate'));
    this.pollenAddon = validPollenAddonContract(this.bounds.pollenAddon, this.bounds.sourceId)
      ? this.bounds.pollenAddon
      : null;
    this.forceQuote = !this.checkoutGate && Boolean(this.bounds.forceQuote || form.hasAttribute('data-force-quote'));
    this.pricingContractRequired = hasPricingContractMetadata(this.tiers)
      || hasAuthoritativeLookupContract(this.lookup);
    this.currentPricingContract = null;

    this.variantInput = form.querySelector('[data-variant-id]');
    this.supplierWidthInput = form.querySelector('[data-supplier-width-cm]');
    this.supplierHeightInput = form.querySelector('[data-supplier-height-cm]');
    this.widthMillimetresInput = form.querySelector('[data-measurement-width-mm]');
    this.heightMillimetresInput = form.querySelector('[data-measurement-height-mm]');
    this.billedWidthInput = form.querySelector('[data-billed-width-cm]');
    this.billedHeightInput = form.querySelector('[data-billed-height-cm]');
    this.billedWidthMillimetresInput = form.querySelector('[data-billed-width-mm]');
    this.billedHeightMillimetresInput = form.querySelector('[data-billed-height-mm]');
    this.configurationKeyInput = form.querySelector('[data-hvo-configuration-key]');
    this.pricingEvidenceVersionInput = form.querySelector('[data-hvo-pricing-evidence-version]');
    this.checkoutGateLink = this.checkoutGate ? form.querySelector('[data-checkout-gate-link]') : null;
    this.forceQuoteLink = this.forceQuote ? form.querySelector('[data-force-quote-link]') : null;
    this.submit = form.querySelector('[data-add-to-cart]');
    this.priceNode = form.querySelector('[data-resolved-price]');
    this.totalNode = form.querySelector('[data-total-price]');
    this.surchargeLines = form.querySelector('[data-surcharge-lines]');
    this.alertNode = form.querySelector('[data-configurator-alert]');
    this.progressSteps = typeof form.querySelectorAll === 'function'
      ? form.querySelectorAll('[data-progress-step]')
      : [];
    this.revealNodes = typeof form.querySelectorAll === 'function'
      ? form.querySelectorAll('[data-configurator-reveal]')
      : [];
    this.customPanel = this.root.querySelector('[data-colour-custom-panel]');
  }
  Configurator.prototype.readSurcharges = function () {
    if (!this.form || typeof this.form.querySelectorAll !== 'function') return [];
    var seen = {};
    return Array.prototype.map.call(
      this.form.querySelectorAll('[data-surcharge]:checked'),
      function (control) {
        var key = control.getAttribute('data-surcharge');
        var surcharge = SURCHARGES[key];
        if (!surcharge || seen[key]) return null;
        seen[key] = true;
        return { key: key, label: surcharge.label, cents: surcharge.cents };
      }
    ).filter(Boolean);
  };

  Configurator.prototype.readAnswers = function () {
    var lookup = this.lookup || {};
    var widthField = lookup.widthSourceName
      ? this.form.querySelector('[data-source-name="' + lookup.widthSourceName + '"]')
      : null;
    var heightField = lookup.heightSourceName
      ? this.form.querySelector('[data-source-name="' + lookup.heightSourceName + '"]')
      : null;
    widthField = widthField || this.form.querySelector('[name="properties[Breedte (cm)]"]');
    heightField = heightField || this.form.querySelector('[name="properties[Hoogte (cm)]"]');
    var colour = this.form.querySelector('[data-colour-standard]:checked');
    var custom = this.form.querySelector('[data-colour-custom]:checked');
    var selection = this.form.querySelector(
      'input[data-selection-source-id]:checked, select option[data-selection-source-id]:checked'
    );
    var mounting = this.form.querySelector('[data-context-choice="mounting"]:checked');
    var opening = this.form.querySelector('[data-context-choice="opening"]:checked');
    var requiresQuote = this.form.querySelector(
      '[data-requires-quote]:checked, [data-option-row-input]:checked'
    );
    var widthMm = widthField ? parseCentimetresToMillimetres(widthField.value) : null;
    var heightMm = heightField ? parseCentimetresToMillimetres(heightField.value) : null;

    return {
      width: widthMm === null ? null : widthMm / 10,
      height: heightMm === null ? null : heightMm / 10,
      widthMm: widthMm,
      heightMm: heightMm,
      profile: colour ? colour.getAttribute('data-profile-id') : null,
      customColour: Boolean(custom),
      selection: selection ? selection.getAttribute('data-selection-source-id') : null,
      mounting: mounting ? mounting.value : null,
      opening: opening ? opening.value : null,
      requiresQuote: Boolean(requiresQuote),
      surcharges: this.readSurcharges()
    };
  };

  Configurator.prototype.translateMeasurements = function (answers) {
    if (answers.widthMm === null || answers.heightMm === null) return null;
    var widthAddMm = 0;
    var heightAddMm = 0;
    if (answers.mounting === 'Op de dag') {
      widthAddMm = Number(this.bounds.onDayWidthAddMm);
      heightAddMm = Number(this.bounds.onDayHeightAddMm);
      if (!Number.isSafeInteger(widthAddMm) || widthAddMm < 0
        || !Number.isSafeInteger(heightAddMm) || heightAddMm < 0) return null;
    }
    return {
      widthMm: answers.widthMm + widthAddMm,
      heightMm: answers.heightMm + heightAddMm
    };
  };

  Configurator.prototype.hasCompleteOrderPayload = function () {
    return Boolean(
      this.variantInput
      && /^[1-9]\d*$/.test(String(this.variantInput.value || '').trim())
      && hasValidPayloadNumber(this.supplierWidthInput, false)
      && hasValidPayloadNumber(this.supplierHeightInput, false)
      && hasValidPayloadNumber(this.widthMillimetresInput, true)
      && hasValidPayloadNumber(this.heightMillimetresInput, true)
      && hasValidPayloadNumber(this.billedWidthInput, false)
      && hasValidPayloadNumber(this.billedHeightInput, false)
      && hasValidPayloadNumber(this.billedWidthMillimetresInput, true)
      && hasValidPayloadNumber(this.billedHeightMillimetresInput, true)
      && (
        !this.pricingContractRequired
        || (
          this.configurationKeyInput
          && isNonBlankString(this.configurationKeyInput.value)
          && this.pricingEvidenceVersionInput
          && isNonBlankString(this.pricingEvidenceVersionInput.value)
        )
      )
    );
  };

  Configurator.prototype.resolve = function (answers) {
    var lookup = this.lookup;
    if (!lookup) return { outcome: OUTCOMES.uncaptured };
    if (
      (this.form.querySelector('[data-context-choice="mounting"]') && !answers.mounting)
      || (this.form.querySelector('[data-context-choice="opening"]') && !answers.opening)
    ) {
      return { outcome: OUTCOMES.incomplete };
    }
    if (answers.requiresQuote) return { outcome: OUTCOMES.uncaptured };
    if (answers.widthMm === null || answers.heightMm === null || (!answers.profile && !answers.customColour)) {
      return { outcome: OUTCOMES.incomplete };
    }

    var supplierMeasurements = this.translateMeasurements(answers);
    if (!supplierMeasurements) return { outcome: OUTCOMES.uncaptured };

    var bounds = this.bounds;
    var widthMinMm = centimetresToMillimetres(bounds.widthMin);
    var heightMinMm = centimetresToMillimetres(bounds.heightMin);
    var widthMaxMm = centimetresToMillimetres(bounds.widthMax);
    var heightMaxMm = centimetresToMillimetres(bounds.heightMax);
    if (widthMinMm && supplierMeasurements.widthMm < widthMinMm) return { outcome: OUTCOMES.underMinimum };
    if (heightMinMm && supplierMeasurements.heightMm < heightMinMm) return { outcome: OUTCOMES.underMinimum };
    if (widthMaxMm && supplierMeasurements.widthMm > widthMaxMm) return { outcome: OUTCOMES.overMaximum };
    if (heightMaxMm && supplierMeasurements.heightMm > heightMaxMm) return { outcome: OUTCOMES.overMaximum };

    var stepMm = centimetresToMillimetres(lookup.gridStepCm);
    var widthMm = roundUpToStep(supplierMeasurements.widthMm, stepMm);
    var heightMm = roundUpToStep(supplierMeasurements.heightMm, stepMm);
    var profiles = lookup.profiles || [];
    var profileIndex = answers.customColour ? resolveProfileIndex(profiles, '9016') : resolveProfileIndex(profiles, answers.profile);
    if (profileIndex < 0) return { outcome: OUTCOMES.uncaptured };

    var selectionIndex = 0;
    var selections = lookup.selections || [];
    if (selections.length > 1) {
      if (!answers.selection) return { outcome: OUTCOMES.incomplete };
      selectionIndex = selections.findIndex(function (entry) {
        return entry.split('=')[1] === answers.selection;
      });
      if (selectionIndex < 0) return { outcome: OUTCOMES.uncaptured };
    }

    var match = (lookup.coordinates || []).find(function (coordinate) {
      return centimetresToMillimetres(coordinate[0]) === widthMm
        && centimetresToMillimetres(coordinate[1]) === heightMm
        && coordinate[2] === profileIndex
        && coordinate[3] === selectionIndex;
    });
    if (!match) return { outcome: OUTCOMES.uncaptured };

    var tierLabel = (lookup.tiers || [])[match[4]];
    var variant = this.tiers[tierLabel];
    if (
      !variant
      || !variant.available
      || !Number.isFinite(Number(variant.price))
      || Number(variant.price) <= 0
      || !variant.id
    ) {
      return { outcome: OUTCOMES.uncaptured };
    }

    var configurationKey = configurationKeyForCoordinate(lookup, match);
    var pricingContract = validatePricingContract(this.tiers, lookup, variant, match, configurationKey);
    var nativeMagneticContract = validateNativeMagneticContract(
      this.tiers,
      lookup,
      answers,
      variant,
      match,
      pricingContract
    );
    return {
      outcome: OUTCOMES.ready,
      variant: variant,
      billedWidth: widthMm / 10,
      billedHeight: heightMm / 10,
      billedWidthMm: widthMm,
      billedHeightMm: heightMm,
      pricingContractValid: pricingContract.valid,
      configurationKey: pricingContract.configurationKey,
      pricingEvidenceVersion: pricingContract.evidenceVersion,
      nativeMagneticContract: nativeMagneticContract
    };
  };

  Configurator.prototype.renderStatus = function (outcome, hasUnsafeSurcharge) {
    if (!this.alertNode) return;
    var messages = {};
    messages[OUTCOMES.incomplete] = this.strings.incomplete || 'Maak uw keuzes compleet. Daarna ziet u direct de prijs.';
    messages[OUTCOMES.underMinimum] = this.strings.underMinimum || 'Deze maat valt buiten het online bestelbereik. We adviseren u graag.';
    messages[OUTCOMES.overMaximum] = this.strings.overMaximum || 'Deze maat valt buiten het online bestelbereik. We adviseren u graag.';
    messages[OUTCOMES.uncaptured] = this.strings.uncaptured || 'Voor deze uitvoering maken we eerst een persoonlijke offerte.';
    messages[OUTCOMES.customColour] = this.strings.customColour || 'Voor deze RAL-kleur maken we eerst een persoonlijke offerte.';
    messages[OUTCOMES.forceQuote] = this.strings.forceQuote || 'Deze uitvoering ronden we samen met u af via een offerte.';
    var text = hasUnsafeSurcharge
      ? 'Deze extra is nog niet veilig online af te rekenen. U kunt deze configuratie daarom alleen als offerte aanvragen.'
      : messages[outcome] || '';
    this.alertNode.textContent = text;
    this.alertNode.hidden = !text;
  };

  Configurator.prototype.renderPrice = function (result, answers) {
    var priceResolved = result.outcome === OUTCOMES.ready;
    var baseCents = priceResolved ? Number(result.variant.price) : null;
    var nativeMagneticCents = priceResolved
      && result.nativeMagneticContract
      && result.nativeMagneticContract.valid
      && hasSurcharge(answers, 'magneticClosure')
      ? result.nativeMagneticContract.surchargeCents
      : 0;
    var surchargeCents = answers.surcharges.reduce(function (total, surcharge) {
      return total + surcharge.cents;
    }, 0);
    var displayedBaseCents = priceResolved ? baseCents - nativeMagneticCents : null;

    if (this.priceNode) {
      this.priceNode.hidden = !priceResolved;
      var valueNode = this.priceNode.querySelector('.hvo-stated__value');
      if (valueNode) valueNode.textContent = priceResolved ? formatEuros(displayedBaseCents) : '';
    }
    if (this.surchargeLines) {
      this.surchargeLines.innerHTML = priceResolved ? answers.surcharges.map(function (surcharge) {
        return '<div class="hvo-price-row"><span>' + surcharge.label + '</span><strong>+ '
          + formatEuros(surcharge.cents) + '</strong></div>';
      }).join('') : '';
    }
    if (this.totalNode) {
      this.totalNode.textContent = priceResolved
        ? formatEuros(baseCents + surchargeCents - nativeMagneticCents)
        : 'Prijs na uw keuzes';
    }
  };
  Configurator.prototype.renderDisclosure = function (answers) {
    var measurementsComplete = answers.widthMm !== null
      && answers.heightMm !== null
      && answers.widthMm > 0
      && answers.heightMm > 0;
    Array.prototype.forEach.call(this.revealNodes, function (node) {
      node.hidden = !measurementsComplete;
    });
  };


  Configurator.prototype.renderProgress = function (answers, result) {
    if (!this.progressSteps || !this.progressSteps.length) return;
    var selectionComplete = !(this.lookup && (this.lookup.selections || []).length > 1)
      || Boolean(answers.selection);
    var completed = {
      mounting: !this.form.querySelector('[data-context-choice="mounting"]') || Boolean(answers.mounting),
      measurements: answers.widthMm !== null && answers.heightMm !== null,
      execution: selectionComplete
        && (!this.form.querySelector('[data-context-choice="opening"]') || Boolean(answers.opening)),
      colour: Boolean(answers.profile || answers.customColour),
      extras: result.outcome === OUTCOMES.ready
    };
    var currentFound = false;
    Array.prototype.forEach.call(this.progressSteps, function (step) {
      var key = step.getAttribute('data-progress-step');
      var isComplete = Boolean(completed[key]);
      var isCurrent = !currentFound && !isComplete;
      step.classList.toggle('is-complete', isComplete);
      step.classList.toggle('is-current', isCurrent);
      if (isCurrent) {
        step.setAttribute('aria-current', 'step');
        currentFound = true;
      } else {
        step.removeAttribute('aria-current');
      }
    });
  };

  Configurator.prototype.collectQuoteEntries = function () {
    var values = [];
    var seen = {};
    var controls = this.form.elements || this.form.querySelectorAll('[name]');
    Array.prototype.forEach.call(controls, function (control) {
      if (!control || !control.name) return;
      if (control.disabled || (control.matches && control.matches(':disabled'))) return;
      if (control.type === 'submit' || control.type === 'image' || control.type === 'button' || control.type === 'reset') return;
      if ((control.type === 'radio' || control.type === 'checkbox') && !control.checked) return;
      if (control.name === 'form_type' || control.name === 'utf8' || control.name === 'id'
        || /^variant(?:[-_]id)?$/.test(control.name)) return;

      var label = '';
      if (control.name === 'quantity') {
        label = 'Aantal';
      } else {
        var property = control.name.match(/^properties\[(.+)\]$/);
        if (!property || property[1].charAt(0) === '_') return;
        label = property[1];
      }
      var text = String(control.value || '').trim();
      if (!text) return;
      var entry = label + ': ' + text;
      if (seen[entry]) return;
      seen[entry] = true;
      values.push(entry);
    });
    return values;
  };

  Configurator.prototype.refreshQuoteLink = function () {
    var link = this.checkoutGateLink || this.forceQuoteLink;
    if (!link || !this.bounds.quoteUrl) return;
    var quoteUrl;
    try {
      quoteUrl = new URL(this.bounds.quoteUrl, window.location.href);
    } catch (error) {
      return;
    }
    var configValues = this.collectQuoteEntries();
    if (configValues.length) quoteUrl.searchParams.set('config', configValues.join('\n'));
    else quoteUrl.searchParams.delete('config');
    link.href = quoteUrl.toString();
  };

  Configurator.prototype.update = function () {
    syncSelectedColourLabel(this.form);
    var answers = this.readAnswers();
    var result = this.resolve(answers);
    var supplierMeasurements = this.translateMeasurements(answers);
    var priceResolved = result.outcome === OUTCOMES.ready;
    var nativeMagneticReady = !result.nativeMagneticContract
      || !result.nativeMagneticContract.required
      || result.nativeMagneticContract.valid;
    var hasUnsafeSurcharge = answers.surcharges.some(function (surcharge) {
      if (surcharge.key === 'pollenMesh') return !this.pollenAddon;
      return surcharge.key !== 'magneticClosure' || !nativeMagneticReady;
    }, this) || answers.customColour;
    var configurationReady = priceResolved
      && result.pricingContractValid
      && nativeMagneticReady
      && !hasUnsafeSurcharge
      && !this.forceQuote
      && !this.checkoutGate;
    this.refreshQuoteLink();
    this.renderDisclosure(answers);

    if (this.customPanel) this.customPanel.hidden = !answers.customColour;
    this.variantInput = this.form.querySelector('[data-variant-id]');
    this.supplierWidthInput = this.form.querySelector('[data-supplier-width-cm]');
    this.supplierHeightInput = this.form.querySelector('[data-supplier-height-cm]');
    this.widthMillimetresInput = this.form.querySelector('[data-measurement-width-mm]');
    this.heightMillimetresInput = this.form.querySelector('[data-measurement-height-mm]');
    this.billedWidthInput = this.form.querySelector('[data-billed-width-cm]');
    this.billedHeightInput = this.form.querySelector('[data-billed-height-cm]');
    this.billedWidthMillimetresInput = this.form.querySelector('[data-billed-width-mm]');
    this.billedHeightMillimetresInput = this.form.querySelector('[data-billed-height-mm]');
    this.configurationKeyInput = this.form.querySelector('[data-hvo-configuration-key]');
    this.pricingEvidenceVersionInput = this.form.querySelector('[data-hvo-pricing-evidence-version]');
    if (this.supplierWidthInput) this.supplierWidthInput.value = supplierMeasurements ? formatCentimetres(supplierMeasurements.widthMm) : '';
    if (this.supplierHeightInput) this.supplierHeightInput.value = supplierMeasurements ? formatCentimetres(supplierMeasurements.heightMm) : '';
    if (this.widthMillimetresInput) this.widthMillimetresInput.value = answers.widthMm === null ? '' : String(answers.widthMm);
    if (this.heightMillimetresInput) this.heightMillimetresInput.value = answers.heightMm === null ? '' : String(answers.heightMm);
    if (this.billedWidthInput) this.billedWidthInput.value = configurationReady ? formatCentimetres(result.billedWidthMm) : '';
    if (this.billedHeightInput) this.billedHeightInput.value = configurationReady ? formatCentimetres(result.billedHeightMm) : '';
    if (this.billedWidthMillimetresInput) this.billedWidthMillimetresInput.value = configurationReady ? String(result.billedWidthMm) : '';
    if (this.billedHeightMillimetresInput) this.billedHeightMillimetresInput.value = configurationReady ? String(result.billedHeightMm) : '';
    if (this.variantInput) this.variantInput.value = configurationReady ? String(result.variant.id) : '';
    if (this.configurationKeyInput) {
      this.configurationKeyInput.value = configurationReady ? result.configurationKey : '';
    }
    if (this.pricingEvidenceVersionInput) {
      this.pricingEvidenceVersionInput.value = configurationReady ? result.pricingEvidenceVersion : '';
    }
    this.currentPricingContract = configurationReady ? {
      variant: result.variant,
      variantId: String(result.variant.id),
      configurationKey: result.configurationKey,
      evidenceVersion: result.pricingEvidenceVersion
    } : null;

    var ready = configurationReady && this.hasCompleteOrderPayload();
    if (!ready) {
      if (this.billedWidthInput) this.billedWidthInput.value = '';
      if (this.billedHeightInput) this.billedHeightInput.value = '';
      if (this.billedWidthMillimetresInput) this.billedWidthMillimetresInput.value = '';
      if (this.billedHeightMillimetresInput) this.billedHeightMillimetresInput.value = '';
      if (this.variantInput) this.variantInput.value = '';
      if (this.configurationKeyInput) this.configurationKeyInput.value = '';
      if (this.pricingEvidenceVersionInput) this.pricingEvidenceVersionInput.value = '';
    }
    if (this.submit) this.submit.disabled = !ready;

    this.renderPrice(result, answers);
    this.renderProgress(answers, result);
    this.renderStatus(this.forceQuote && priceResolved ? OUTCOMES.forceQuote : result.outcome, hasUnsafeSurcharge);
  };

  Configurator.prototype.addToCart = function (event) {
    event.preventDefault();
    this.update();
    if (
      this.forceQuote
      || this.checkoutGate
      || !this.submit
      || this.submit.disabled
      || !this.hasCompleteOrderPayload()
    ) {
      if (this.submit) this.submit.disabled = true;
      return;
    }
    var self = this;
    var original = this.submit.textContent;
    var formData = new FormData(this.form);
    var diagnosticFlags = cartContractDiagnosticFlags(
      formData,
      this.lookup,
      this.tiers,
      this.currentPricingContract
    );
    var pollenSelected = hasSurcharge(this.readAnswers(), 'pollenMesh');
    this.submit.disabled = true;
    if (this.strings.adding) this.submit.textContent = this.strings.adding;

    function showAddFailure(diagnostic) {
      reportCartDiagnostic(diagnostic);
      self.submit.textContent = original;
      self.update();
      self.renderStatusText(self.strings.addFailed || 'Toevoegen is niet gelukt. Probeer het opnieuw.');
    }

    function navigateAfterRefreshFailure(status) {
      reportCartDiagnostic(cartDiagnostic('refresh', status, null, diagnosticFlags));
      window.location.assign(self.bounds.cartUrl);
    }

    function refreshCart() {
      return fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) {
            navigateAfterRefreshFailure(response.status);
            return;
          }
          return Promise.resolve()
            .then(function () { return response.json(); })
            .then(function (cart) {
              try {
                document.dispatchEvent(new CustomEvent('hvo:cart-updated', { detail: { itemCount: cart.item_count } }));
              } catch (error) {
                navigateAfterRefreshFailure(response.status);
                return;
              }
              window.location.assign(self.bounds.cartUrl);
            }, function () {
              navigateAfterRefreshFailure(response.status);
            });
        }, function () {
          navigateAfterRefreshFailure(null);
        });
    }
    var requestBody = formData;
    var requestHeaders = { Accept: 'application/javascript' };
    if (pollenSelected) {
      var pollenPayload = this.pollenAddon
        ? buildPollenCartPayload(formData, this.pollenAddon)
        : null;
      if (!pollenPayload) {
        showAddFailure(cartDiagnostic('add', null, null, diagnosticFlags));
        return Promise.resolve();
      }
      requestBody = JSON.stringify(pollenPayload);
      requestHeaders = {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      };
    }


    return fetch(this.bounds.cartAddUrl, {
      method: 'POST',
      headers: requestHeaders,
      body: requestBody
    }).then(function (response) {
      if (response.ok) return refreshCart();
      return readAddFailureDiagnostic(response, diagnosticFlags).then(showAddFailure);
    }, function () {
      showAddFailure(cartDiagnostic('add', null, null, diagnosticFlags));
    });
  };

  Configurator.prototype.renderStatusText = function (text) {
    if (!this.alertNode) return;
    this.alertNode.textContent = text;
    this.alertNode.hidden = false;
  };

  Configurator.prototype.start = function () {
    var self = this;
    this.form.addEventListener('input', function () { self.update(); });
    this.form.addEventListener('change', function () { self.update(); });
    this.form.addEventListener('submit', function (event) { return self.addToCart(event); });
    this.update();
  };

  function bindSteppers(root) {
    (root || document).querySelectorAll('[data-stepper]').forEach(function (stepper) {
      var input = stepper.querySelector('[data-stepper-input]');
      if (!input || stepper.hasAttribute('data-stepper-bound')) return;
      stepper.setAttribute('data-stepper-bound', '');
      function nudge(delta) {
        var min = input.min === '' ? -Infinity : Number(input.min);
        var max = input.max === '' ? Infinity : Number(input.max);
        var current = parseCentimetresToMillimetres(input.value);
        var base = current === null ? min : current / 10;
        var next = Math.min(max, Math.max(min, base + delta));
        input.value = String(next).replace('.', ',');
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      var down = stepper.querySelector('[data-stepper-down]');
      var up = stepper.querySelector('[data-stepper-up]');
      if (down) down.addEventListener('click', function () { nudge(-1); });
      if (up) up.addEventListener('click', function () { nudge(1); });
    });
  }

  function bindGallery(root) {
    var gallery = (root || document).querySelector('[data-gallery]');
    if (!gallery || gallery.hasAttribute('data-gallery-bound')) return;
    gallery.setAttribute('data-gallery-bound', '');
    gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var index = thumb.getAttribute('data-gallery-thumb');
        gallery.querySelectorAll('[data-gallery-frame]').forEach(function (frame) {
          frame.hidden = frame.getAttribute('data-gallery-frame') !== index;
        });
        gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (other) {
          other.setAttribute('aria-current', other === thumb ? 'true' : 'false');
        });
      });
    });
  }

  var infoDocumentBound = false;

  function closeInfoPopover(popover, restoreFocus) {
    if (!popover) return;
    var button = popover.querySelector('[data-info-toggle]');
    var panel = popover.querySelector('[data-info-panel]');
    popover.removeAttribute('data-info-pinned');
    if (button) button.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
    if (restoreFocus && button) button.focus();
  }

  function openInfoPopover(popover, pinned) {
    var button = popover.querySelector('[data-info-toggle]');
    var panel = popover.querySelector('[data-info-panel]');
    if (pinned) popover.setAttribute('data-info-pinned', '');
    if (button) button.setAttribute('aria-expanded', 'true');
    if (panel) panel.hidden = false;
  }

  function bindInfoPopovers(root) {
    (root || document).querySelectorAll('[data-info-popover]').forEach(function (popover) {
      if (popover.hasAttribute('data-info-bound')) return;
      popover.setAttribute('data-info-bound', '');
      var button = popover.querySelector('[data-info-toggle]');
      if (!button) return;
      popover.addEventListener('mouseenter', function () { openInfoPopover(popover, false); });
      popover.addEventListener('mouseleave', function () {
        if (!popover.hasAttribute('data-info-pinned')) closeInfoPopover(popover, false);
      });
      popover.addEventListener('focusin', function () { openInfoPopover(popover, false); });
      popover.addEventListener('focusout', function (event) {
        if (!popover.contains(event.relatedTarget) && !popover.hasAttribute('data-info-pinned')) {
          closeInfoPopover(popover, false);
        }
      });
      button.addEventListener('click', function (event) {
        event.stopPropagation();
        if (popover.hasAttribute('data-info-pinned')) closeInfoPopover(popover, false);
        else openInfoPopover(popover, true);
      });
    });

    if (!infoDocumentBound && typeof document.addEventListener === 'function') {
      infoDocumentBound = true;
      document.addEventListener('click', function (event) {
        document.querySelectorAll('[data-info-popover][data-info-pinned]').forEach(function (popover) {
          if (!popover.contains(event.target)) closeInfoPopover(popover, false);
        });
      });
      document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        var open = document.querySelector('[data-info-popover] [data-info-toggle][aria-expanded="true"]');
        if (open) closeInfoPopover(open.closest('[data-info-popover]'), true);
      });
    }
  }

  function captureCustomerState(form) {
    if (!form) return [];
    return Array.prototype.reduce.call(form.querySelectorAll('input[name], select[name], textarea[name]'), function (state, control) {
      if (!control.name || control.disabled || control.name === 'id' || control.name === 'form_type' || control.name === 'utf8') return state;
      var property = control.name.match(/^properties\[(.+)\]$/);
      if (property && property[1].charAt(0) === '_') return state;
      if ((control.type === 'radio' || control.type === 'checkbox') && !control.checked) return state;
      state.push({ name: control.name, type: control.type, value: control.value });
      return state;
    }, []);
  }

  function restoreCustomerState(form, state) {
    if (!form) return;
    Array.prototype.forEach.call(form.querySelectorAll('input[name], select[name], textarea[name]'), function (control) {
      var matches = state.filter(function (entry) { return entry.name === control.name; });
      if (!matches.length) return;
      if (control.type === 'radio' || control.type === 'checkbox') {
        control.checked = matches.some(function (entry) { return entry.value === control.value; });
      } else {
        control.value = matches[0].value;
      }
    });
  }

  function invalidateProductSurface(surface, message) {
    if (!surface) return;
    surface.setAttribute('aria-busy', 'true');
    surface.classList.add('is-switching');
    var variant = surface.querySelector('[data-variant-id]');
    var submit = surface.querySelector('[data-add-to-cart]');
    var price = surface.querySelector('[data-resolved-price]');
    var surchargeLines = surface.querySelector('[data-surcharge-lines]');
    var total = surface.querySelector('[data-total-price]');
    var alert = surface.querySelector('[data-configurator-alert]');
    var configurationKey = surface.querySelector('[data-hvo-configuration-key]');
    var pricingEvidenceVersion = surface.querySelector('[data-hvo-pricing-evidence-version]');
    if (variant) variant.value = '';
    if (configurationKey) configurationKey.value = '';
    if (pricingEvidenceVersion) pricingEvidenceVersion.value = '';
    if (submit) submit.disabled = true;
    if (price) price.hidden = true;
    if (surchargeLines) surchargeLines.innerHTML = '';
    if (total) total.textContent = 'Prijs na uw keuzes';
    if (alert) {
      alert.textContent = message;
      alert.hidden = false;
    }
    surface.querySelectorAll('[data-sibling-product]').forEach(function (link) {
      link.setAttribute('aria-disabled', 'true');
    });
  }

  function finishProductSurface(surface) {
    if (!surface) return;
    surface.removeAttribute('aria-busy');
    surface.classList.remove('is-switching');
    surface.querySelectorAll('[data-sibling-product]').forEach(function (link) {
      link.removeAttribute('aria-disabled');
    });
  }

  var siblingRequestSequence = 0;

  function buildSiblingTargetUrl(href) {
    var currentUrl = new URL(window.location.href);
    var linkedUrl = new URL(href, currentUrl);
    var targetUrl = new URL(linkedUrl.pathname, currentUrl.origin);
    var previewThemeId = currentUrl.searchParams.get('preview_theme_id');
    if (previewThemeId) targetUrl.searchParams.set('preview_theme_id', previewThemeId);
    if (linkedUrl.hash) targetUrl.hash = linkedUrl.hash;
    return targetUrl;
  }

  function loadSiblingProduct(href, updateHistory) {
    var currentSurface = document.querySelector('[data-product-surface]');
    if (!currentSurface) return Promise.reject(new Error('product-surface-missing'));
    var currentForm = currentSurface.querySelector('[data-configurator]');
    var sectionId = currentSurface.getAttribute('data-product-section-id');
    var currentWrapper = currentSurface.closest('[id^="shopify-section-"]');
    if (!sectionId || !currentWrapper) return Promise.reject(new Error('product-section-missing'));
    var requestSequence = ++siblingRequestSequence;

    var state = captureCustomerState(currentForm);
    var anchor = currentSurface.querySelector('[data-configurator-step="execution"]');
    var anchorTop = anchor ? anchor.getBoundingClientRect().top : null;
    var targetUrl = buildSiblingTargetUrl(href);
    var sectionUrl = new URL(targetUrl.href);
    sectionUrl.searchParams.set('section_id', sectionId);
    invalidateProductSurface(currentSurface, 'Uitvoering laden…');

    return fetch(sectionUrl.toString(), { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('product-section-request-failed');
        return response.text();
      })
      .then(function (html) {
        if (requestSequence !== siblingRequestSequence) throw new Error('product-section-stale');
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var nextSurface = parsed.querySelector('[data-product-surface]');
        if (!nextSurface) throw new Error('product-section-invalid');
        var renderedUrl = new URL(nextSurface.getAttribute('data-product-url'), targetUrl);
        if (renderedUrl.pathname !== targetUrl.pathname) throw new Error('product-section-mismatch');
        var nextWrapper = nextSurface.closest('[id^="shopify-section-"]');
        if (requestSequence !== siblingRequestSequence) throw new Error('product-section-stale');
        currentWrapper.innerHTML = nextWrapper ? nextWrapper.innerHTML : parsed.body.innerHTML;

        var activeSurface = currentWrapper.querySelector('[data-product-surface]');
        var activeForm = activeSurface && activeSurface.querySelector('[data-configurator]');
        if (!activeSurface || !activeForm) throw new Error('product-form-missing');
        restoreCustomerState(activeForm, state);
        startSurface(currentWrapper);

        if (updateHistory && window.history && typeof window.history.pushState === 'function') {
          window.history.pushState({ hvoProductSwitch: true }, '', targetUrl.pathname + targetUrl.search + targetUrl.hash);
        }
        var nextAnchor = activeSurface.querySelector('[data-configurator-step="execution"]');
        if (anchorTop !== null && nextAnchor && typeof window.scrollBy === 'function') {
          window.scrollBy(0, nextAnchor.getBoundingClientRect().top - anchorTop);
        }
        var selected = activeSurface.querySelector('[data-sibling-product][aria-current="page"]');
        if (selected && typeof selected.focus === 'function') selected.focus({ preventScroll: true });
        finishProductSurface(activeSurface);
      })
      .catch(function (error) {
        if (requestSequence !== siblingRequestSequence || error.message === 'product-section-stale') return;
        var activeSurface = document.querySelector('[data-product-surface]');
        invalidateProductSurface(activeSurface, 'Wisselen is niet gelukt. Probeer het opnieuw.');
        finishProductSurface(activeSurface);
      });
  }

  var productSwitchingBound = false;

  function bindProductSwitching() {
    if (productSwitchingBound || typeof document.addEventListener !== 'function') return;
    productSwitchingBound = true;
    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('[data-sibling-product]');
      if (!link) return;
      event.preventDefault();
      if (link.getAttribute('aria-current') === 'page' || link.getAttribute('aria-disabled') === 'true') return;
      loadSiblingProduct(link.href, true);
    });
    if (typeof window.addEventListener === 'function') {
      window.addEventListener('popstate', function () {
        loadSiblingProduct(window.location.href, false);
      });
    }
  }

  function startSurface(root) {
    var scope = root || document;
    bindSteppers(scope);
    bindGallery(scope);
    bindInfoPopovers(scope);
    bindProductSwitching();
    var form = scope.querySelector('[data-configurator]');
    if (form) new Configurator(form).start();
  }

  function start() {
    startSurface(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
